import { useEffect, useRef, lazy, Suspense } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import SEO from '@/components/seo/SEO'
import { useAuth } from '@/context/AuthContext'
import { ROLES } from '@/config/roles'
import { redirectBasedOnRole } from '@/utils/redirectBasedOnRole'
import { trackBuyingJourney } from '@/services/customerJourneyService'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS,
  LANDING_VISITED_KEY
} from '@/constants/buyingJourney'
import HeroSection from './newCompoenent/HeroSection/LpHeroSection'
import TeachKids from './newCompoenent/teachKids/TeachKids'
import HowItWorks from './newCompoenent/howItWorks/HowItWorks'

import './NewLandingPage.css'

const DarkSection = lazy(
  () => import('@/pages/home/educator/components/MissionCurriculum')
)
const PopularLessons = lazy(
  () => import('@/pages/home/educator/components/PopularLessons')
)
const StayConnect = lazy(() => import('./newCompoenent/StayConnect'))
const FrequentlyAskedQuestions = lazy(
  () => import('./newCompoenent/faq/FrequentlyAskedQuestions')
)
const HelpChild = lazy(() => import('./newCompoenent/helpChild/HelpChild'))

const SectionFallback = () => <div style={{ minHeight: '120px' }} aria-hidden />

const NewLandingPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    currentUser,
    userRole,
    loading: authLoading,
    emailVerified
  } = useAuth()

  useEffect(() => {
    if (authLoading) return
    if (!currentUser) return
    if (userRole === ROLES.UNDEFINED || userRole === 'UNDEFINED') {
      navigate('/auth/role-select', { replace: true })
      return
    }
    const needsVerification =
      (userRole === ROLES.PARENT || userRole === ROLES.SCHOOL_ADMIN) &&
      emailVerified === false
    if (needsVerification) {
      navigate('/register/verify-email', { replace: true })
      return
    }
    if (userRole) {
      redirectBasedOnRole(userRole, currentUser, navigate, emailVerified)
    }
  }, [authLoading, currentUser, userRole, navigate, emailVerified])

  const landingTracked = useRef(false)
  useEffect(() => {
    const path = location.pathname
    if (path !== '/' && path !== '/home') return
    if (landingTracked.current) return
    const hasVisited =
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(LANDING_VISITED_KEY)) ||
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem(LANDING_VISITED_KEY))
    if (hasVisited) return
    landingTracked.current = true
    trackBuyingJourney({
      stage: JOURNEY_FUNNEL_STAGES.A_LANDING,
      eventName: JOURNEY_FUNNEL_EVENTS.LANDING_VIEW
    })
    try {
      if (typeof sessionStorage !== 'undefined')
        sessionStorage.setItem(LANDING_VISITED_KEY, '1')
      if (typeof localStorage !== 'undefined')
        localStorage.setItem(LANDING_VISITED_KEY, '1')
    } catch (_) {
      // ignore
    }
  }, [location.pathname])

  useEffect(() => {
    const section = location.state?.scrollToSection
    if (section !== 'how-it-works') return

    let attempts = 0
    let timer = null
    const maxAttempts = 12

    const scrollToHowItWorks = () => {
      const el = document.getElementById('how-it-works')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        navigate('.', { replace: true, state: {} })
        return
      }
      attempts += 1
      if (attempts < maxAttempts) {
        timer = setTimeout(scrollToHowItWorks, 100)
      }
    }

    timer = setTimeout(scrollToHowItWorks, 150)
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [location.state?.scrollToSection, navigate])

  if (
    !authLoading &&
    currentUser &&
    userRole &&
    userRole !== ROLES.UNDEFINED &&
    userRole !== 'UNDEFINED'
  ) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '100vh' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='new-landing-page' id='home-page'>
      <SEO
        title='Online Safety Education for Kids'
        description='Digipalz teaches kids online safety, digital literacy, and cyber awareness through fun, interactive lessons, quizzes, and games. Trusted by parents, teachers, and schools.'
        url='/'
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Digipalz — Online Safety Education for Kids',
          description:
            'Interactive online safety education platform for children, parents, and schools.',
          url: 'https://www.digipalz.io/'
        }}
      />
      <HeroSection />
      <TeachKids />
      <HowItWorks />
      <div className='dark-section-home'>
        <Suspense fallback={<SectionFallback />}>
          <DarkSection />
        </Suspense>
      </div>
      <div className='popular-lessons-home'>
        <Suspense fallback={<SectionFallback />}>
          <PopularLessons />
        </Suspense>
      </div>
      <Suspense fallback={<SectionFallback />}>
        <StayConnect />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <FrequentlyAskedQuestions />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <HelpChild />
      </Suspense>
    </div>
  )
}

export default NewLandingPage
